import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { subscribeToImpersonationChannel } from "./impersonationCable";
import {
  acceptImpersonationRequest,
  cancelImpersonationRequest,
  createImpersonationRequest,
  declineImpersonationRequest,
  endImpersonationAccess,
  enterImpersonation,
  fetchImpersonationState,
  stopImpersonation,
} from "./impersonationApi";
import {
  AccessingMeBar,
  AdminImpersonationBar,
  IncomingRequestModal,
  OutgoingRequestCard,
  RequestReasonModal,
  ToastStack,
} from "./ImpersonationUI";
import "./impersonation.css";

const FLASH_KEY = "impersonation:flash";
const COLLAPSED_KEY = "impersonation:strip-collapsed";

const END_MESSAGES = {
  admin_exit: "You have exited impersonation.",
  user_revoked: "The user ended your access to their account.",
  tab_closed: "Impersonation ended because no tab was open.",
  admin_signed_out: "Impersonation ended because you signed out.",
  user_signed_out: "Impersonation ended because the user signed out.",
  target_banned: "Impersonation ended because the account was suspended.",
};

export const ImpersonationContext = createContext({
  canRequest: false,
  impersonating: false,
  outgoing: null,
  requestImpersonation: () => {},
});

const storeFlash = (message) => {
  try {
    sessionStorage.setItem(FLASH_KEY, message);
  } catch (error) {}
};

const readCollapsed = () => {
  try {
    return sessionStorage.getItem(COLLAPSED_KEY) === "true";
  } catch (error) {
    return false;
  }
};

const takeFlash = () => {
  try {
    const message = sessionStorage.getItem(FLASH_KEY);
    sessionStorage.removeItem(FLASH_KEY);
    return message;
  } catch (error) {
    return null;
  }
};

const currentPath = () => `${window.location.pathname}${window.location.search}`;

const ImpersonationProvider = ({ children }) => {
  const [state, setState] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [reasonTarget, setReasonTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [now, setNow] = useState(Date.now());
  const offsetRef = useRef(0);
  const enteringRef = useRef(false);
  const leavingRef = useRef(false);
  const toastIdRef = useRef(0);

  const pushToast = useCallback((message, variant = "info") => {
    toastIdRef.current += 1;
    const id = toastIdRef.current;
    setToasts((prev) => [...prev, { id, message, variant }]);
    setTimeout(() => setToasts((prev) => prev.filter((toast) => toast.id !== id)), 6000);
  }, []);

  const syncClock = (payload) => {
    if (payload?.server_time) offsetRef.current = Date.parse(payload.server_time) - Date.now();
  };

  const loadState = useCallback(async () => {
    const { ok, data } = await fetchImpersonationState();
    if (!ok || !data?.impersonation) return;
    const impersonation = data.impersonation;
    syncClock(impersonation.session || impersonation.outgoing || impersonation.incoming?.[0] || impersonation.accessing_me?.[0]);
    setState({ ...impersonation, me: data.current_user, trueMe: data.true_user });
  }, []);

  useEffect(() => {
    const flash = takeFlash();
    if (flash) pushToast(flash);
    loadState();
  }, [loadState, pushToast]);

  const role = state?.role;
  const adminSession = role === "admin" ? state?.session : null;
  const subscriptionKey = role ? `${role}:${adminSession?.id || ""}` : null;

  const leaveTo = useCallback((path, message) => {
    if (leavingRef.current) return;
    leavingRef.current = true;
    if (message) storeFlash(message);
    window.location.href = path || "/";
  }, []);

  const handleMessage = useCallback((message) => {
    const { event, request } = message || {};
    if (!request) return;
    syncClock(request);

    if (role === "admin") {
      if (event === "session_ended" && adminSession && request.id === adminSession.id) {
        leaveTo(request.return_to, END_MESSAGES[request.end_reason] || "Impersonation has ended.");
      }
      return;
    }

    const meId = state?.me?.id;
    if (request.requester?.id === meId) {
      const name = request.target?.name || "The user";
      switch (event) {
        case "request_accepted":
          setState((prev) => ({ ...prev, outgoing: request }));
          break;
        case "request_declined":
          setState((prev) => ({ ...prev, outgoing: null }));
          pushToast(`${name} declined your access request.`, "warning");
          break;
        case "request_expired":
          setState((prev) => ({ ...prev, outgoing: null }));
          pushToast(`${name} did not respond in time. The request expired.`, "warning");
          break;
        case "enter_expired":
          setState((prev) => ({ ...prev, outgoing: null }));
          pushToast(`The access ${name} granted has expired. Send a new request to try again.`, "warning");
          break;
        case "request_cancelled":
          setState((prev) => ({ ...prev, outgoing: null }));
          break;
        case "session_started":
          if (!enteringRef.current) window.location.reload();
          break;
        default:
          break;
      }
    }

    if (request.target?.id === meId) {
      switch (event) {
        case "request_created":
          setState((prev) => ({
            ...prev,
            incoming: [...(prev?.incoming || []).filter((item) => item.id !== request.id), request],
          }));
          break;
        case "request_closed":
          setState((prev) => ({ ...prev, incoming: (prev?.incoming || []).filter((item) => item.id !== request.id) }));
          break;
        case "session_started":
          setState((prev) => ({
            ...prev,
            incoming: (prev?.incoming || []).filter((item) => item.id !== request.id),
            accessing_me: [...(prev?.accessing_me || []).filter((item) => item.id !== request.id), request],
          }));
          break;
        case "session_ended":
          setState((prev) => ({ ...prev, accessing_me: (prev?.accessing_me || []).filter((item) => item.id !== request.id) }));
          pushToast(`${request.requester?.name || "The admin"} no longer has access to your account.`);
          break;
        default:
          break;
      }
    }
  }, [role, adminSession, state?.me?.id, leaveTo, pushToast]);

  const handleMessageRef = useRef(handleMessage);
  useEffect(() => {
    handleMessageRef.current = handleMessage;
  }, [handleMessage]);

  useEffect(() => {
    if (!subscriptionKey) return undefined;
    const params = adminSession?.id ? { impersonation_session_id: adminSession.id } : {};
    return subscribeToImpersonationChannel(params, (message) => handleMessageRef.current(message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionKey]);

  const hasCountdown = !!(state?.outgoing || state?.incoming?.length);
  useEffect(() => {
    if (!hasCountdown) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [hasCountdown]);

  const serverNow = now + offsetRef.current;

  useEffect(() => {
    if (!state) return;
    const outgoing = state.outgoing;
    const deadline = outgoing && Date.parse(outgoing.status === "accepted" ? outgoing.enter_expires_at : outgoing.expires_at);
    const outgoingExpired = outgoing && deadline && deadline <= serverNow;
    const incoming = (state.incoming || []).filter((item) => Date.parse(item.expires_at) > serverNow);
    if (outgoingExpired || incoming.length !== (state.incoming || []).length) {
      setState((prev) => ({ ...prev, outgoing: outgoingExpired ? null : prev.outgoing, incoming }));
    }
  }, [serverNow, state]);

  const requestImpersonation = useCallback((user) => {
    if (!user?.id) return;
    setReasonTarget(user);
  }, []);

  const submitRequest = async (reason) => {
    if (!reasonTarget) return;
    setBusy(true);
    const { ok, data, error } = await createImpersonationRequest(reasonTarget.id, reason, currentPath());
    setBusy(false);
    if (!ok) {
      pushToast(error, "danger");
      return;
    }
    syncClock(data.request);
    setReasonTarget(null);
    setState((prev) => ({ ...prev, outgoing: data.request }));
  };

  const cancelOutgoing = async () => {
    const outgoing = state?.outgoing;
    if (!outgoing) return;
    setBusy(true);
    await cancelImpersonationRequest(outgoing.id);
    setBusy(false);
    setState((prev) => ({ ...prev, outgoing: null }));
  };

  const enterOutgoing = async () => {
    const outgoing = state?.outgoing;
    if (!outgoing) return;
    setBusy(true);
    enteringRef.current = true;
    const { ok, data, error } = await enterImpersonation(outgoing.id);
    if (!ok) {
      enteringRef.current = false;
      setBusy(false);
      pushToast(error, "danger");
      setState((prev) => ({ ...prev, outgoing: null }));
      return;
    }
    window.location.href = data?.redirect_to || "/";
  };

  const exitImpersonation = async () => {
    setBusy(true);
    leavingRef.current = true;
    const { data } = await stopImpersonation();
    storeFlash(data?.message || END_MESSAGES.admin_exit);
    window.location.href = data?.redirect_to || adminSession?.return_to || "/";
  };

  const respondIncoming = async (request, accept) => {
    setBusy(true);
    const { ok, error } = accept ? await acceptImpersonationRequest(request.id) : await declineImpersonationRequest(request.id);
    setBusy(false);
    setState((prev) => ({ ...prev, incoming: (prev?.incoming || []).filter((item) => item.id !== request.id) }));
    if (!ok) pushToast(error, "danger");
  };

  const endAccess = async (session) => {
    setBusy(true);
    const { ok, error } = await endImpersonationAccess(session.id);
    setBusy(false);
    if (!ok) {
      pushToast(error, "danger");
      return;
    }
    setState((prev) => ({ ...prev, accessing_me: (prev?.accessing_me || []).filter((item) => item.id !== session.id) }));
  };

  const showBar = !!adminSession || !!state?.accessing_me?.length;

  const toggleCollapsed = (value) => {
    setCollapsed(value);
    try {
      sessionStorage.setItem(COLLAPSED_KEY, String(value));
    } catch (error) {}
  };

  const contextValue = useMemo(() => ({
    canRequest: role === "self" && !!state?.can_impersonate,
    impersonating: role === "admin",
    outgoing: state?.outgoing || null,
    requestImpersonation,
  }), [role, state?.can_impersonate, state?.outgoing, requestImpersonation]);

  const incoming = state?.incoming?.[0];

  return (
    <ImpersonationContext.Provider value={contextValue}>
      {showBar && (collapsed ? (
        <button
          type="button"
          className={`impersonation-expand-btn${adminSession ? "" : " impersonation-expand-btn--target"}`}
          onClick={() => toggleCollapsed(false)}
          aria-label="Show impersonation bar"
          title="Show impersonation bar"
        >
          {adminSession ? "Impersonating" : "Admin access active"}
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M7 10l5 5 5-5z" fill="currentColor" />
          </svg>
        </button>
      ) : (
        <div className="impersonation-strip">
          {adminSession && (
            <AdminImpersonationBar session={adminSession} busy={busy} onExit={exitImpersonation} onCollapse={() => toggleCollapsed(true)} />
          )}
          {(state?.accessing_me || []).map((session) => (
            <AccessingMeBar key={session.id} session={session} busy={busy} onEnd={() => endAccess(session)} onCollapse={() => toggleCollapsed(true)} />
          ))}
        </div>
      ))}
      {children}
      {reasonTarget && (
        <RequestReasonModal
          target={reasonTarget}
          consentBypass={!!state?.consent_bypass}
          busy={busy}
          onSubmit={submitRequest}
          onClose={() => setReasonTarget(null)}
        />
      )}
      {incoming && (
        <IncomingRequestModal
          request={incoming}
          serverNow={serverNow}
          busy={busy}
          onAccept={() => respondIncoming(incoming, true)}
          onDecline={() => respondIncoming(incoming, false)}
        />
      )}
      {state?.outgoing && (
        <OutgoingRequestCard
          request={state.outgoing}
          serverNow={serverNow}
          busy={busy}
          onCancel={cancelOutgoing}
          onEnter={enterOutgoing}
        />
      )}
      <ToastStack toasts={toasts} />
    </ImpersonationContext.Provider>
  );
};

export default ImpersonationProvider;
